import type { ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { alpha } from '@mui/material/styles';
import { Box, ButtonBase, Typography } from '@mui/material';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import type { FieldBaseProps } from './fields/types';

export interface BpChannelOption {
  /** The bit this option contributes to the mask (e.g. Email=1, SMS=2). */
  value: number;
  label: string;
  icon?: ReactNode;
}

export interface BpChannelGroupProps<T extends FieldValues> extends FieldBaseProps<T> {
  /** The combinable options; each contributes one bit to the mask. */
  options: BpChannelOption[];
}

/**
 * A combinable multi-select toggle group bound to a **`[Flags]` integer
 * bitmask** (e.g. `NotificationChannels`: Email=1, SMS=2, both=3, none=0).
 * RHF-bound — `field.value` is the integer mask; toggling a chip flips its bit.
 * Domain-agnostic: the caller supplies the bit/label options.
 */
export function BpChannelGroup<T extends FieldValues>({
  control,
  name,
  label,
  helperText,
  disabled,
  options,
}: BpChannelGroupProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const mask = Number(field.value ?? 0);
        const has = (bit: number) => (mask & bit) === bit && bit !== 0;
        const toggle = (bit: number) => field.onChange(has(bit) ? mask & ~bit : mask | bit);
        return (
          <Box>
            {label && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.75 }}>
                {label}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              {options.map((opt) => {
                const on = has(opt.value);
                return (
                  <ButtonBase
                    key={opt.value}
                    role="switch"
                    aria-checked={on}
                    aria-label={opt.label}
                    disabled={disabled}
                    onClick={() => toggle(opt.value)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: '14px',
                      height: 40,
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: on ? 'primary.main' : 'divider',
                      bgcolor: (t) => (on ? alpha(t.palette.primary.main, 0.1) : 'transparent'),
                      color: on ? 'primary.main' : 'text.secondary',
                      fontSize: 13,
                      fontWeight: on ? 600 : 500,
                    }}
                  >
                    {on ? <CheckOutlined sx={{ fontSize: 16 }} /> : opt.icon}
                    {opt.label}
                  </ButtonBase>
                );
              })}
            </Box>
            {helperText && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.75 }}>
                {helperText}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
}
